import { useEffect, useState } from 'react';
import type { CambioDeFecha, Cancelacion, ImpactoCambioFecha, ImpactoCancelacion } from '../../api/ops/eventos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { Ventana } from '../../componentes/Ventana';
import { cifraDinero, dinero } from '../../logica/dinero';
import { avisoBreveDeError } from '../../logica/estados';
import type { PropsPestana } from './Ficha';
import { Aviso, Ceja, CampoDiaHora, Cifras, Lineas } from './piezas';
import { aCampo, deCampo, informeCambio, diaYHoraEn, errorMotivo, horaEn, plazoVigente, rangoEn, rutaPedido, textoErrorGuardar } from './reglas';

/** Pestañas Fecha (10.4), Cancelar (10.5) e Informe de la cancelación. */

const CONSULTA_CANCELACION_MS = 3000;

type Tipo = 'correction' | 'real_change';

export function CambiarFecha({ api, negocio, datos, soloLectura, movil, membresia, yo, navegar, setAccion, recargarFicha }: PropsPestana) {
    const e = datos.evento;
    const zona = e.timezone;
    const avisar = useAvisoBreve();
    const [paso, setPaso] = useState<'form' | 'impacto' | 'hecho'>('form');
    const [inicio, setInicio] = useState(aCampo(e.starts_at, zona));
    const [fin, setFin] = useState(aCampo(e.ends_at, zona));
    const [tipo, setTipo] = useState<Tipo | null>(null);
    const [avisarA, setAvisarA] = useState(true);
    const [hasta, setHasta] = useState('');
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [impacto, setImpacto] = useState<ImpactoCambioFecha | null>(null);
    const [confirmar, setConfirmar] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [visto, setVisto] = useState<CambioDeFecha | null>(null);

    const nuevoInicio = deCampo(inicio, zona);
    const nuevoFin = deCampo(fin, zona);
    const errores: Record<string, string> = {};
    if (intentado) {
        if (nuevoInicio === null) {
            errores.inicio = 'Elige el día y la hora.';
        }
        if (nuevoFin === null || (nuevoInicio !== null && nuevoFin <= nuevoInicio)) {
            errores.fin = 'Termina después de empezar.';
        }
        if (nuevoInicio === e.starts_at && nuevoFin === e.ends_at) {
            errores.inicio = 'Es la misma fecha que ahora.';
        }
        if (tipo === null) {
            errores.tipo = 'Elige por qué cambia.';
        }
        const m = errorMotivo(motivo);
        if (m !== null) {
            errores.motivo = m;
        }
    }
    const listo = () => {
        setIntentado(true);
        return nuevoInicio !== null && nuevoFin !== null && nuevoFin > nuevoInicio && !(nuevoInicio === e.starts_at && nuevoFin === e.ends_at) && tipo !== null && errorMotivo(motivo) === null;
    };
    const plazoHasta = tipo === 'real_change' && avisarA ? (hasta === '' ? nuevoInicio : deCampo(hasta, zona)) : null;

    const revisar = () => {
        if (!listo() || nuevoInicio === null || nuevoFin === null) {
            return;
        }
        if (tipo === 'correction') {
            setConfirmar(true);
            return;
        }
        setOcupado(true);
        api.previewEventDateChange(negocio, e.id, { new_starts_at: nuevoInicio, new_ends_at: nuevoFin })
            .then((r) => {
                setOcupado(false);
                setImpacto(r);
                setPaso('impacto');
            })
            .catch((err: unknown) => {
                setOcupado(false);
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
            });
    };

    const cambiar = () => {
        if (nuevoInicio === null || nuevoFin === null || tipo === null) {
            return;
        }
        setOcupado(true);
        api.changeEventDate(
            negocio,
            e.id,
            {
                new_starts_at: nuevoInicio,
                new_ends_at: nuevoFin,
                kind: tipo,
                reason: motivo.trim(),
                notify_attendees: tipo === 'real_change' && avisarA,
                ...(tipo === 'real_change' && avisarA && plazoHasta !== null ? { free_cancellation_until: plazoHasta } : {}),
            },
            datos.version,
        )
            .then((c) => {
                setOcupado(false);
                setConfirmar(false);
                setVisto(c);
                setPaso('hecho');
                recargarFicha();
                avisar({ tipo: 'ok', texto: tipo === 'correction' ? 'Fecha corregida.' : 'Fecha cambiada.' });
            })
            .catch((err: unknown) => {
                setOcupado(false);
                setConfirmar(false);
                if (err instanceof ErrorApi && err.codigo === 'version_conflict') {
                    recargarFicha();
                }
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
            });
    };

    useEffect(() => {
        if (soloLectura || paso === 'hecho') {
            setAccion(null);
        } else if (paso === 'form') {
            setAccion({ texto: tipo === 'correction' ? 'Corregir la fecha' : 'Revisar el impacto', icono: tipo === 'correction' ? 'calendar-clock' : 'arrow-right', onAccion: revisar, ocupado, ocupadoTexto: 'Calculando…' });
        } else {
            setAccion({ texto: 'Cambiar la fecha', icono: 'calendar-clock', onAccion: () => { setConfirmar(true); }, inactiva: impacto?.fits === false });
        }
        return () => { setAccion(null); };
        // `revisar` cambia en cada pintada; basta con lo que se ve.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soloLectura, paso, tipo, ocupado, impacto, inicio, fin, motivo, avisarA, hasta]);

    if (soloLectura) {
        return <Aviso tono="gris" icono="lock">El negocio está en solo lectura: ahora no se puede cambiar la fecha. Si no se va a celebrar, puedes cancelarlo.</Aviso>;
    }

    const quien = (c: CambioDeFecha) => (membresia !== undefined && c.member_id === membresia.member_id ? yo.account.display_name : 'Alguien del equipo');
    const anterior = plazoVigente(e.date_changes);

    if (paso === 'hecho' || visto !== null) {
        const c = visto ?? (e.date_changes ?? [])[0];
        if (c === undefined) {
            return null;
        }
        const real = c.kind === 'real_change';
        const abierto = real && c.notify_attendees && c.free_cancellation_until != null && new Date(c.free_cancellation_until) > new Date();
        return (
            <div className="rv-ev-form">
                <Aviso tono="negro" icono={real ? 'bell-ring' : 'pencil'} titulo={real ? (c.notify_attendees ? 'Fecha cambiada y aviso enviado' : 'Fecha cambiada sin aviso') : 'Fecha corregida'}>
                    {real
                        ? c.notify_attendees
                            ? `Avisamos a quien tiene entrada, invitación o reservado.${abierto ? ` Pueden cancelar gratis hasta el ${diaYHoraEn(c.free_cancellation_until, zona).toLocaleLowerCase('es')}.` : ' El plazo para cancelar gratis ya terminó o lo cerró un cambio posterior.'}`
                            : 'Nadie ha recibido aviso y se cierra cualquier plazo anterior para cancelar gratis.'
                        : 'Nadie ha recibido aviso: queda en Actividad.'}
                </Aviso>
                {real && <Cifras items={informeCambio(c)} />}
                <dl className="rv-ev-filas">
                    <div><dt>Antes</dt><dd>{rangoEn(c.previous_starts_at, c.previous_ends_at, zona)}</dd></div>
                    <div><dt>Ahora</dt><dd>{rangoEn(c.new_starts_at, c.new_ends_at, zona)}</dd></div>
                    {c.reason !== undefined && <div><dt>Motivo</dt><dd>{c.reason}</dd></div>}
                    {c.free_cancellation_until != null && <div><dt>Cancelar gratis hasta</dt><dd>{diaYHoraEn(c.free_cancellation_until, zona)}</dd></div>}
                    <div><dt>Lo hizo</dt><dd>{`${quien(c)} · ${diaYHoraEn(c.created_at, zona)}`}</dd></div>
                </dl>
                <span className="rv-ev-botones">
                    <Boton tipo="secundario" icono="receipt" onClick={() => { navegar(`/pedidos?apartado=pedidos&evento=${encodeURIComponent(e.id)}`); }}>
                        Ver los pedidos
                    </Boton>
                    <Boton tipo="secundario" icono="calendar-clock" onClick={() => { setVisto(null); setPaso('form'); setImpacto(null); setTipo(null); setMotivo(''); setIntentado(false); setInicio(aCampo(e.starts_at, zona)); setFin(aCampo(e.ends_at, zona)); }}>
                        Cambiar otra vez
                    </Boton>
                </span>
            </div>
        );
    }

    const ventana = (
        <Ventana
            abierta={confirmar}
            titulo={tipo === 'correction' ? '¿Corregir la fecha?' : '¿Cambiar la fecha?'}
            texto={tipo === 'correction' ? `Pasa a ${rangoEn(nuevoInicio ?? undefined, nuevoFin ?? undefined, zona)}. Nadie recibe aviso; queda en Actividad.` : `Pasa a ${rangoEn(nuevoInicio ?? undefined, nuevoFin ?? undefined, zona)}.${avisarA ? ' Avisamos a compradores, invitados y reservados.' : ' Sin aviso a nadie.'}`}
            accion={tipo === 'correction' ? 'Corregir' : 'Cambiar la fecha'}
            accionIcono="calendar-clock"
            ocupado={ocupado}
            ocupadoTexto="Cambiando…"
            movil={movil}
            onAccion={cambiar}
            onCerrar={() => { setConfirmar(false); }}
        />
    );

    if (paso === 'impacto' && impacto !== null) {
        const invitados = datos.invitados?.active_guests ?? null;
        return (
            <div className="rv-ev-form">
                {impacto.fits ? (
                    <Aviso tono="negro" icono="circle-check" titulo={`Cabe el ${diaYHoraEn(nuevoInicio, zona).toLocaleLowerCase('es')}`}>El aforo y el cupo de taquilla caben en la nueva fecha.</Aviso>
                ) : (
                    <Aviso tono="rojo" icono="circle-alert" titulo="La nueva fecha no cabe">
                        {(impacto.reasons ?? []).includes('capacity_exceeded') ? 'En la sede no quedan plazas para las comprometidas. Elige otra fecha.' : 'Elige otra fecha.'}
                    </Aviso>
                )}
                {(impacto.same_day_events ?? []).length > 0 && (
                    <Aviso tono="amarillo" icono="calendar-days" titulo={(impacto.same_day_events ?? []).length === 1 ? 'Ese día también hay otro evento en la sede.' : `Ese día también hay ${String((impacto.same_day_events ?? []).length)} eventos más en la sede.`}>
                        Quien reserve mesa ese día elegirá a qué evento va.
                    </Aviso>
                )}
                {anterior !== null && (
                    <Aviso tono="amarillo" icono="clock" titulo="Ya hay un plazo abierto para cancelar gratis.">
                        {`Hasta el ${diaYHoraEn(anterior.free_cancellation_until, zona).toLocaleLowerCase('es')}, de un cambio anterior. Con este cambio se cierra${avisarA ? ' y vale el de este cambio' : ''}.`}
                    </Aviso>
                )}
                <Cifras
                    items={[
                        { titulo: 'Pedidos', cifra: String(impacto.affected_orders), nota: 'con entradas' },
                        { titulo: 'Entradas', cifra: String(impacto.affected_tickets), nota: 'vendidas' },
                        ...(invitados === null ? [] : [{ titulo: 'Invitados', cifra: String(invitados), nota: 'de las listas' }]),
                    ]}
                />
                <Lineas
                    items={[
                        {
                            icono: 'bell-ring',
                            titulo: 'Avisos',
                            texto: avisarA ? `Por email a ${String(impacto.affected_orders)} compradores con la nueva fecha y un enlace para mantener o cancelar gratis. Listas y Reservas avisan a sus invitados y reservados.` : 'Sin aviso: nadie recibe nada.',
                        },
                        ...(avisarA
                            ? [{
                                icono: 'rotate-ccw' as const,
                                titulo: 'Cancelación gratis',
                                texto: `Hasta el ${diaYHoraEn(plazoHasta, zona).toLocaleLowerCase('es')}, con reembolso íntegro.${impacto.refundable_amount === undefined ? '' : ` Si cancelaran todos, se devolverían ${dinero(impacto.refundable_amount)}.`}`,
                            }]
                            : []),
                        { icono: 'qr-code', titulo: 'Pases', texto: 'Siguen valiendo: el QR no cambia, solo su fecha.' },
                        { icono: 'armchair', titulo: 'Reservados', texto: 'Se recolocan en la nueva fecha.' },
                        { icono: 'ticket', titulo: 'Venta', texto: 'El evento sigue publicado y a la venta.' },
                    ]}
                />
                <span className="rv-ev-botones">
                    <Boton tipo="secundario" icono="chevron-left" onClick={() => { setPaso('form'); }}>
                        Cambiar algo
                    </Boton>
                </span>
                {ventana}
            </div>
        );
    }

    return (
        <div className="rv-ev-form">
            <div className="rv-ev-ahora">
                <span>Ahora</span>
                <strong>{rangoEn(e.starts_at, e.ends_at, zona)}</strong>
            </div>
            <div className="rv-ca-dos">
                <CampoDiaHora etiqueta="Nueva fecha · empieza" valor={inicio} onChange={setInicio} error={errores.inicio} />
                <CampoDiaHora etiqueta="Termina" valor={fin} onChange={setFin} error={errores.fin} />
            </div>
            <Ceja>Por qué cambia</Ceja>
            <div className="rv-ev-opciones rv-ev-opciones--dos" role="radiogroup" aria-label="Por qué cambia">
                <OpcionRadio elegida={tipo === 'correction'} onElegir={() => { setTipo('correction'); }} titulo="Corrección de un error" texto="Se puso mal. Nadie recibe aviso; queda en Actividad." />
                <OpcionRadio elegida={tipo === 'real_change'} onElegir={() => { setTipo('real_change'); }} titulo="Cambio real" texto="Lluvia, una baja… Avisamos y pueden cancelar gratis." />
            </div>
            {errores.tipo !== undefined && <span className="rv-ev-error" role="alert">{errores.tipo}</span>}
            {tipo === 'real_change' && (
                <>
                    <Interruptor etiqueta="Avisar a quien tiene entrada, invitación o reservado" icono="bell-ring" valor={avisarA} onChange={setAvisarA} ayuda={avisarA ? 'Y pueden cancelar gratis hasta la fecha que elijas.' : 'Sin aviso no se abre la cancelación gratis y se cierra cualquier plazo anterior.'} />
                    {avisarA && (
                        <CampoDiaHora etiqueta="Pueden cancelar gratis hasta" valor={hasta === '' ? (nuevoInicio === null ? '' : aCampo(nuevoInicio, zona)) : hasta} onChange={setHasta} ayuda={`Como mucho, hasta que empiece${nuevoInicio === null ? '' : ` (${horaEn(nuevoInicio, zona)})`}.`} />
                    )}
                    {anterior !== null && <span className="rv-ev-texto">{`Hay un plazo abierto hasta el ${diaYHoraEn(anterior.free_cancellation_until, zona).toLocaleLowerCase('es')}: este cambio lo cierra.`}</span>}
                </>
            )}
            <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} error={errores.motivo} ayuda="Queda en Actividad con tu nombre." filas={3} max={500} />
            {(e.date_changes ?? []).length > 0 && (
                <section aria-label="Cambios de fecha">
                    <Ceja>Cambios de fecha</Ceja>
                    <ul className="rv-ev-historial">
                        {[...(e.date_changes ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((c) => (
                            <li key={c.id}>
                                <button type="button" className="rv-ev-enlace" onClick={() => { setVisto(c); setPaso('hecho'); }}>
                                    <span className="rv-en-celda">
                                        <strong>{`${c.kind === 'correction' ? 'Corrección' : 'Cambio real'} · ${diaYHoraEn(c.created_at, zona)}`}</strong>
                                        <small>{`${rangoEn(c.previous_starts_at, c.previous_ends_at, zona)} → ${rangoEn(c.new_starts_at, c.new_ends_at, zona)}`}</small>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {ventana}
        </div>
    );
}

export function Cancelar({ api, negocio, datos, movil, setAccion, irPestana, recargarFicha }: PropsPestana) {
    const e = datos.evento;
    const avisar = useAvisoBreve();
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [impacto, setImpacto] = useState<ImpactoCancelacion | null>(null);
    const [confirmar, setConfirmar] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const error = intentado ? errorMotivo(motivo) : null;

    const revisar = () => {
        setIntentado(true);
        if (errorMotivo(motivo) !== null) {
            return;
        }
        setOcupado(true);
        api.previewEventCancellation(negocio, e.id)
            .then((r) => { setOcupado(false); setImpacto(r); })
            .catch((err: unknown) => { setOcupado(false); avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) }); });
    };

    const cancelar = () => {
        setOcupado(true);
        api.cancelEvent(negocio, e.id, motivo.trim(), datos.version)
            .then(() => {
                setOcupado(false);
                setConfirmar(false);
                avisar({ tipo: 'ok', texto: 'Evento cancelado: los reembolsos están en marcha.' });
                irPestana('informe');
                recargarFicha();
            })
            .catch((err: unknown) => {
                setOcupado(false);
                setConfirmar(false);
                if (err instanceof ErrorApi && err.codigo === 'version_conflict') {
                    recargarFicha();
                }
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
            });
    };

    useEffect(() => {
        setAccion(impacto === null ? { texto: 'Revisar el impacto', icono: 'arrow-right', onAccion: revisar, ocupado, ocupadoTexto: 'Calculando…' } : { texto: 'Cancelar el evento', icono: 'calendar-x', onAccion: () => { setConfirmar(true); } });
        return () => { setAccion(null); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impacto, ocupado, motivo]);

    const invitados = datos.invitados?.active_guests ?? null;
    return (
        <div className="rv-ev-form">
            {impacto === null ? (
                <>
                    <Lineas
                        items={[
                            { icono: 'rotate-ccw', titulo: 'Se devuelve todo', texto: 'Reembolso íntegro con Stripe, también los gastos de gestión.' },
                            { icono: 'banknote', titulo: 'Lo pagado en el local', texto: 'Efectivo o datáfono: lo devuelves tú en el local.' },
                            { icono: 'bell-ring', titulo: 'Avisos', texto: 'Avisamos a compradores, invitados y reservados.' },
                        ]}
                    />
                    <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} error={error} ayuda="Lo reciben los compradores en el aviso y queda en Actividad." filas={3} max={500} />
                    <span className="rv-ev-texto">No se puede deshacer.</span>
                </>
            ) : (
                <>
                    <Cifras
                        items={[
                            { titulo: 'A devolver', cifra: cifraDinero(impacto.amount_to_refund), unidad: '€', nota: impacto.booking_fees_included === false ? 'Sin gastos de gestión' : 'Gastos de gestión incluidos', oscura: true },
                            { titulo: 'Pedidos', cifra: String(impacto.orders), nota: 'se cancelan' },
                            ...(impacto.tickets === undefined ? [] : [{ titulo: 'Entradas', cifra: String(impacto.tickets), nota: 'se anulan' }]),
                            ...(invitados === null ? [] : [{ titulo: 'Invitados', cifra: String(invitados), nota: 'se retiran' }]),
                        ]}
                    />
                    <Lineas
                        items={[
                            { icono: 'rotate-ccw', titulo: 'Reembolso', texto: `${dinero(impacto.amount_to_refund)} con Stripe a la tarjeta de cada comprador${impacto.booking_fees_included === false ? '' : ', gastos de gestión incluidos'}.` },
                            ...(impacto.external_payments > 0 ? [{ icono: 'banknote' as const, titulo: 'Pagos en el local', texto: `${String(impacto.external_payments)} ${impacto.external_payments === 1 ? 'pedido se pagó' : 'pedidos se pagaron'} en efectivo o datáfono: devuélvelos tú en el local; aquí quedan anotados.` }] : []),
                            ...(e.sale_mode !== 'tickets' ? [{ icono: 'list-checks' as const, titulo: 'Listas', texto: invitados === null ? 'Se cierran y se retiran sus invitados.' : `Se cierran y se retiran sus ${String(invitados)} invitados.` }] : []),
                            { icono: 'armchair', titulo: 'Reservados', texto: 'Se cancelan y se reembolsan.' },
                            { icono: 'bell-ring', titulo: 'Avisos', texto: 'Por email a cada comprador con el motivo; Listas y Reservas avisan a los suyos.' },
                            { icono: 'eye-off', titulo: 'Web', texto: 'Desaparece de tu web, el widget y el asistente. No se puede deshacer.' },
                        ]}
                    />
                    <span className="rv-ev-botones">
                        <Boton tipo="secundario" icono="chevron-left" onClick={() => { setImpacto(null); }}>
                            Cambiar algo
                        </Boton>
                    </span>
                </>
            )}
            <Ventana
                abierta={confirmar}
                titulo={`¿Cancelar «${e.name ?? ''}»?`}
                texto={impacto === null ? '' : `Se devuelven ${dinero(impacto.amount_to_refund)} a ${String(impacto.orders)} pedidos y se avisa a todos. No se puede deshacer.`}
                accion="Cancelar el evento"
                accionIcono="calendar-x"
                cancelar="No, volver"
                ocupado={ocupado}
                ocupadoTexto="Cancelando…"
                movil={movil}
                onAccion={cancelar}
                onCerrar={() => { setConfirmar(false); }}
            />
        </div>
    );
}

export function Informe({ api, negocio, datos, navegar }: PropsPestana) {
    const e = datos.evento;
    const [leida, setC] = useState<Cancelacion | null>(null);
    const [error, setError] = useState(false);
    const c = leida ?? datos.cancelacion;

    useEffect(() => {
        if (c !== null && c.status !== 'in_progress') {
            return;
        }
        const leer = () => {
            api.getEventCancellation(negocio, e.id)
                .then((r) => { setC(r); setError(false); })
                .catch(() => { setError(true); });
        };
        if (c === null) {
            leer();
        }
        const t = window.setInterval(leer, CONSULTA_CANCELACION_MS);
        return () => { window.clearInterval(t); };
    }, [api, negocio, e.id, c]);

    if (c === null) {
        return error ? <Aviso tono="rojo" icono="circle-alert" titulo="No se ha podido leer el informe.">Prueba de nuevo en un momento.</Aviso> : <p className="rv-ev-texto">Leyendo el informe…</p>;
    }
    const enCurso = c.status === 'in_progress';
    const fallidos = c.failed_orders ?? [];
    const pct = c.orders_total === 0 ? 100 : Math.round(((c.orders_refunded + c.refunds_failed) / c.orders_total) * 100);
    return (
        <div className="rv-ev-form">
            <div className="rv-ev-aviso rv-ev-aviso--negro rv-ev-progreso" role="status">
                <span className="rv-ev-aviso__texto">
                    <strong>{enCurso ? 'Devolviendo el dinero…' : c.refunds_failed > 0 ? `Evento cancelado, con ${c.refunds_failed === 1 ? 'un reembolso pendiente' : `${String(c.refunds_failed)} reembolsos pendientes`}` : 'Evento cancelado y todo devuelto'}</strong>
                    {!enCurso && c.completed_at != null && <span>{`Terminado el ${diaYHoraEn(c.completed_at, e.timezone).toLocaleLowerCase('es')}.`}</span>}
                </span>
                <span className="rv-ev-progreso__barra" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                    <span style={{ width: `${String(pct)}%` }} />
                </span>
                <small>{`${String(c.orders_refunded)} de ${String(c.orders_total)} pedidos reembolsados con Stripe`}</small>
            </div>
            <Cifras
                items={[
                    { titulo: 'Devuelto', cifra: cifraDinero(c.amount_refunded), unidad: '€', nota: `de ${dinero(c.amount_to_refund)}` },
                    { titulo: 'Cancelados', cifra: String(c.orders_total), unidad: 'pedidos', nota: 'con el evento' },
                    { titulo: 'Reembolsados', cifra: String(c.orders_refunded), unidad: 'pedidos', nota: 'con Stripe' },
                    { titulo: 'Fallidos', cifra: String(c.refunds_failed), unidad: c.refunds_failed === 1 ? 'pedido' : 'pedidos', nota: c.refunds_failed > 0 ? 'Hay que mirarlos' : 'Ninguno' },
                ]}
            />
            {fallidos.length > 0 && (
                <Aviso tono="rojo" icono="circle-alert" titulo={fallidos.length === 1 ? 'Un reembolso no se ha podido hacer.' : `${String(fallidos.length)} reembolsos no se han podido hacer.`}>
                    Reintenta el reembolso desde el pedido o devuélvelo por otra vía.
                </Aviso>
            )}
            {fallidos.length > 0 && (
                <ul className="rv-ev-historial" aria-label="Reembolsos fallidos">
                    {fallidos.map((id) => (
                        <li key={id}>
                            <span className="rv-en-celda">
                                <strong>{`Pedido ${id}`}</strong>
                                <small>Reembolso fallido</small>
                            </span>
                            <Boton tipo="secundario" icono="receipt" onClick={() => { navegar(rutaPedido(id)); }}>
                                Ver el pedido
                            </Boton>
                        </li>
                    ))}
                </ul>
            )}
            <dl className="rv-ev-filas">
                {c.reason !== undefined && <div><dt>Motivo</dt><dd>{c.reason}</dd></div>}
                <div><dt>Cancelado</dt><dd>{diaYHoraEn(c.created_at, e.timezone)}</dd></div>
                <div><dt>Avisos</dt><dd>A cada comprador por email; Listas y Reservas, a los suyos</dd></div>
            </dl>
        </div>
    );
}
